import apiFetch from 'react-scale/lib/api-fetch';
import { TemplateVariablesConfig } from '@scale/shared/taskTemplate/requestTypes';
import { fetchTaskTemplate, validateTaskTemplate } from 'react-scale/corp/lib/api/taskTemplate';
import {
  CreateAuditRubricBody,
  EditAuditRubricBody,
  UpdateAuditRubricStatusBody,
  AuditRubric,
  AuditRubricInfo,
  AuditRubricStatus,
  TaskType,
  PaginatedAuditRubricInfo,
} from '@scale/shared-types';
import { populateTemplate } from '@scale/shared/taskTemplate/helpers';
import {
  MultiTurnInteractionConfiguration,
  MultiTurnInteractionConfigurationV2,
} from '@scale/shared/types/chat';

const serializeNetworkAuditRubric = (rubric: any) => ({
  ...rubric,
  approvedAt: rubric.approvedAt ? new Date(rubric.approvedAt) : undefined,
  createdAt: new Date(rubric.createdAt),
  updatedAt: new Date(rubric.updatedAt),
});

export async function getAuditRubrics(
  params: {
    status?: AuditRubricStatus[];
    projectId?: string;
    page?: number;
    limit?: number;
    sort?: string;
    sortOrder?: 'asc' | 'desc';
  } = {},
): Promise<PaginatedAuditRubricInfo> {
  const result = await apiFetch(null, `internal/audit-rubric`, {
    method: 'GET',
    query: params,
  });
  return {
    ...result,
    data: result.data.map(serializeNetworkAuditRubric),
  };
}

export async function getAuditRubricsForProject(projectId: string): Promise<AuditRubricInfo[]> {
  const result = await apiFetch(null, `internal/audit-rubric/project/${projectId}`, {
    method: 'GET',
  });
  return result.map(serializeNetworkAuditRubric);
}

export async function getAuditRubric(rubricId: string): Promise<AuditRubric> {
  const result = await apiFetch(null, `internal/audit-rubric/rubric/${rubricId}`, {
    method: 'GET',
  });
  return serializeNetworkAuditRubric(result);
}

export async function getLiveRubricForProject(projectId: string): Promise<AuditRubric> {
  const result = await apiFetch(null, `internal/audit-rubric/project/${projectId}/live`, {
    method: 'GET',
  });
  return serializeNetworkAuditRubric(result);
}

export type TaskTemplateData = {
  taskParams?: MultiTurnInteractionConfiguration | MultiTurnInteractionConfigurationV2;
  templateVariables?: Record<string, string>;
  templateVariablesConfiguration?: Record<string, TemplateVariablesConfig>;
  taskType: TaskType;
  error?: string;
};

export async function getAndValidateTaskTemplate(projectId: string): Promise<TaskTemplateData> {
  const latestTaskTemplate = await fetchTaskTemplate(projectId);
  if (!latestTaskTemplate.taskTemplate) {
    throw new Error('No task template found');
  }
  const {
    params: taskParams,
    templateVariables,
    templateVariablesConfiguration,
  } = latestTaskTemplate.taskTemplate;
  const populatedTemplate = await populateTemplate(
    taskParams,
    templateVariables ?? {},
    undefined,
    templateVariablesConfiguration,
  );
  const { valid, message } = await validateTaskTemplate(latestTaskTemplate.type, populatedTemplate);
  return valid
    ? {
        taskParams: taskParams as
          | MultiTurnInteractionConfiguration
          | MultiTurnInteractionConfigurationV2,
        templateVariables: templateVariables ?? {},
        templateVariablesConfiguration,
        taskType: latestTaskTemplate.type,
      }
    : {
        error: message,
        taskType: latestTaskTemplate.type,
      };
}

export async function createAuditRubric(
  projectId: string,
  requestData: CreateAuditRubricBody,
): Promise<AuditRubric> {
  const result = await apiFetch(null, `internal/audit-rubric/project/${projectId}`, {
    method: 'POST',
    body: requestData,
  });
  return result;
}

export async function editAuditRubric(
  rubricId: string,
  requestData: EditAuditRubricBody,
): Promise<AuditRubric> {
  const result = await apiFetch(null, `internal/audit-rubric/rubric/${rubricId}`, {
    method: 'PUT',
    body: requestData,
  });
  return result;
}

export async function updateAuditRubricStatus(
  projectId: string,
  rubricId: string,
  requestData: UpdateAuditRubricStatusBody,
): Promise<AuditRubric> {
  const result = await apiFetch(
    null,
    `internal/audit-rubric/project/${projectId}/rubric/${rubricId}/status`,
    {
      method: 'PUT',
      body: requestData,
    },
  );
  return result;
}
